import { ErrorPage } from '@/components/ErrorPage';
import { TablePageLayout } from '@/layouts/TablePageLayout';
import { useApiTokens } from '@/queries/api-tokens';
import PlusOutlined from '@ant-design/icons/PlusOutlined';
import Tag from 'antd/es/tag';
import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { StatusTag, useDataTableState } from 'ui-components';
import type { DataTableColumnType } from 'ui-components';

import type { ApiTokenResponse } from '@mai/platform-api/src/routes/api-tokens.schema';

import { ApiTokensTable } from './components/ApiTokensTable';
import { CreateTokenModal } from './components/CreateTokenModal';
import { EditTokenModal } from './components/EditTokenModal';

export const ApiTokensPage: React.FC = () => {
  const { teamId } = useParams<{ teamId: string }>();
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedToken, setSelectedToken] = useState<ApiTokenResponse | null>(
    null,
  );

  const { searchParams } = useDataTableState();

  const {
    data: tokensResponse,
    isLoading,
    error,
  } = useApiTokens(teamId || null);

  const handleEdit = (token: ApiTokenResponse) => {
    setSelectedToken(token);
    setEditModalOpen(true);
  };

  const handleView = (token: ApiTokenResponse) => {
    // For now, just open edit modal - can expand to view-only modal later
    handleEdit(token);
  };

  const handleCloseModals = () => {
    setCreateModalOpen(false);
    setEditModalOpen(false);
    setSelectedToken(null);
  };

  const columns: DataTableColumnType<ApiTokenResponse>[] = useMemo(
    () => [
      {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
        ellipsis: true,
        sorter: (a, b) => a.name.localeCompare(b.name),
        filter: {
          type: 'search',
          placeholder: 'Search by name...',
        },
      },
      {
        title: 'Token',
        key: 'token',
        width: 150,
        render: (_, record) => (
          <span style={{ fontFamily: 'monospace' }}>
            {record.tokenPrefix}...{record.last4}
          </span>
        ),
      },
      {
        title: 'Role',
        dataIndex: 'role',
        key: 'role',
        width: 120,
        render: (role: string) => {
          const color =
            {
              admin: 'red',
              member: 'blue',
              readonly: 'green',
            }[role] || 'default';

          return <Tag color={color}>{role.toUpperCase()}</Tag>;
        },
        sorter: (a, b) => a.role.localeCompare(b.role),
        filter: {
          type: 'select',
          placeholder: 'All Roles',
          options: [
            { label: 'Admin', value: 'admin' },
            { label: 'Member', value: 'member' },
            { label: 'Read Only', value: 'readonly' },
          ],
        },
      },
      {
        title: 'Last Used',
        dataIndex: 'lastUsedAt',
        key: 'lastUsedAt',
        width: 150,
        render: (date: string | null) => {
          if (!date) return 'Never';
          const now = new Date();
          const lastUsed = new Date(date);
          const diffInMs = now.getTime() - lastUsed.getTime();
          const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

          if (diffInDays === 0) return 'Today';
          if (diffInDays === 1) return 'Yesterday';
          if (diffInDays < 7) return `${diffInDays} days ago`;
          if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} weeks ago`;
          return `${Math.floor(diffInDays / 30)} months ago`;
        },
        sorter: (a, b) =>
          new Date(a.lastUsedAt || 0).getTime() -
          new Date(b.lastUsedAt || 0).getTime(),
      },
      {
        title: 'Usage (7d)',
        dataIndex: 'usage7Days',
        key: 'usage7Days',
        width: 100,
        align: 'right',
        render: (count: number) => count.toLocaleString(),
        sorter: (a, b) => a.usage7Days - b.usage7Days,
      },
      {
        title: 'Expires',
        dataIndex: 'expiresAt',
        key: 'expiresAt',
        width: 150,
        render: (date: string | null) => {
          if (!date) return 'Never';
          const expiresAt = new Date(date);
          const now = new Date();

          if (expiresAt < now) {
            return <Tag color="error">Expired</Tag>;
          }

          const diffInMs = expiresAt.getTime() - now.getTime();
          const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));

          if (diffInDays === 0) return <Tag color="warning">Expires today</Tag>;
          if (diffInDays === 1)
            return <Tag color="warning">Expires tomorrow</Tag>;
          if (diffInDays < 7)
            return <Tag color="warning">{diffInDays} days</Tag>;
          if (diffInDays < 30) return `${Math.floor(diffInDays / 7)} weeks`;
          return `${Math.floor(diffInDays / 30)} months`;
        },
        sorter: (a, b) =>
          new Date(a.expiresAt || 0).getTime() -
          new Date(b.expiresAt || 0).getTime(),
      },
      {
        title: 'Status',
        dataIndex: 'isActive',
        key: 'status',
        width: 120,
        render: (isActive: boolean, record: ApiTokenResponse) => {
          if (!isActive) {
            return <StatusTag status="inactive" type="generic" />;
          }

          if (record.expiresAt && new Date(record.expiresAt) < new Date()) {
            return (
              <StatusTag status="expired" type="generic">
                Expired
              </StatusTag>
            );
          }

          return <StatusTag status="active" type="generic" />;
        },
        filter: {
          type: 'select',
          placeholder: 'All Statuses',
          options: [
            { label: 'Active', value: 'active' },
            { label: 'Inactive', value: 'inactive' },
            { label: 'Expired', value: 'expired' },
          ],
        },
      },
    ],
    [],
  );

  const page = parseInt(searchParams.get('page') || '1', 10);
  const pageSize = parseInt(searchParams.get('pageSize') || '20', 10);

  // Apply client-side filtering and pagination
  const { paginatedTokens, totalCount } = useMemo(() => {
    const tokens = tokensResponse?.items || [];
    let filtered = [...tokens];

    const role = searchParams.get('role');
    const status = searchParams.get('status');
    const name = searchParams.get('name');

    // Apply role filter
    if (role) {
      filtered = filtered.filter((token) => token.role === role);
    }

    // Apply status filter
    if (status) {
      filtered = filtered.filter((token) => {
        if (status === 'active') {
          return (
            token.isActive &&
            (!token.expiresAt || new Date(token.expiresAt) > new Date())
          );
        }
        if (status === 'inactive') {
          return !token.isActive;
        }
        if (status === 'expired') {
          return (
            token.isActive &&
            token.expiresAt &&
            new Date(token.expiresAt) < new Date()
          );
        }
        return true;
      });
    }

    // Apply search filter
    if (name) {
      const searchLower = name.toLowerCase();
      filtered = filtered.filter(
        (token) =>
          token.name.toLowerCase().includes(searchLower) ||
          token.tokenId.toLowerCase().includes(searchLower),
      );
    }

    const totalCount = filtered.length;

    // Apply pagination
    const start = (page - 1) * pageSize;
    const end = start + pageSize;
    const paginatedTokens = filtered.slice(start, end);

    return { paginatedTokens, totalCount };
  }, [tokensResponse?.items, searchParams, page, pageSize]);

  if (error) {
    return <ErrorPage />;
  }

  return (
    <TablePageLayout
      title="API Tokens"
      description="Create and manage API tokens for programmatic access to your team's resources."
      actions={[
        {
          label: 'Create Token',
          type: 'primary',
          icon: <PlusOutlined />,
          onClick: () => setCreateModalOpen(true),
        },
      ]}
    >
      <ApiTokensTable
        columns={columns}
        tokens={paginatedTokens}
        loading={isLoading}
        onEdit={handleEdit}
        onView={handleView}
        onCreateToken={() => setCreateModalOpen(true)}
        pagination={{
          current: page,
          pageSize: pageSize,
          total: totalCount,
          showSizeChanger: true,
          showQuickJumper: false,
          showTotal: (total: number, range: [number, number]) =>
            `${range[0]}-${range[1]} of ${total} items`,
        }}
      />

      <CreateTokenModal
        open={createModalOpen}
        onCancel={handleCloseModals}
        teamId={teamId || ''}
      />

      <EditTokenModal
        open={editModalOpen}
        onCancel={handleCloseModals}
        token={selectedToken}
      />
    </TablePageLayout>
  );
};
